import React, { ChangeEvent, useEffect, useMemo, useState } from 'react';
import styles from './ProcessRefund.module.scss';
import {
  getCurrentPlanForOrder,
  getFullBundleRefundAmount,
  getOrderPlanOptions,
  getRefundActionForPayload,
  getRefundForOptions,
  hasTransactionsForOrder,
  REFUND_ACTIONS,
  REFUND_FOR,
  refundActionsOptions,
} from './refundOptions.helpers';
import { useDispatch, useSelector } from 'react-redux';
import {
  bundleDetailsSelector,
  orderDetailsSelector,
  plansSelector,
} from '../../selectors/bundleSelectors';
import { ORDER_TYPES } from '../../helpers/constants';
import { TOrder, TOrderRefundDetails, TPlan } from '../../types/bundle.types';
import CustomButton from '../Common/Button/Button';
import {
  getLatestDomainOrder,
  getLatestMailOrder,
  getLatestSiteOrder,
} from '../../helpers/product.helpers';
import { authTokenSelector } from '../../selectors/userSelectors';
import { toast } from 'react-toastify';
import { processRefund } from '../../actions/orderActions';
import { getCurrencySymbol } from '../utils';
import { SourceSelector } from '../SourceSelector';
import { useIntl } from 'react-intl';
import { SOURCE_CHANGE_OPTIONS, SOURCE_HOOKS } from '../../utils/const';
import medusa from '../../utils/medusa';
import { toCapitalize } from '../../utils/helper';

const { MAIL, SITE, DOMAIN } = ORDER_TYPES;

interface Props {
  orderRefundDetails: TOrderRefundDetails[];
  currency: string;
  startDate: string;
  closeModal: () => void;
}

const initialFormErrors = {
  numberOfAccounts: false,
  mailRefundAmount: false,
  siteRefundAmount: false,
  domainRefundAmount: false,
  refundReason: false,
  source: false,
  otherSource: false,
};

const RefundOptions = ({
  orderRefundDetails,
  currency,
  startDate,
  closeModal,
}: Props) => {
  const intl = useIntl();
  const [refundFor, setRefundFor] = useState<
    (typeof REFUND_FOR)[keyof typeof REFUND_FOR] | ''
  >('');
  const [refundAction, setRefundAction] = useState<
    (typeof REFUND_ACTIONS)[keyof typeof REFUND_ACTIONS] | ''
  >('');
  const [showRefundForOptions, setShowRefundForOptions] = useState(true);
  const [mailPlanId, setMailPlanId] = useState<number | null>(null);
  const [sitePlanId, setSitePlanId] = useState<number | null>(null);
  const [isRefundProcessing, setIsRefundProcessing] = useState<boolean>(false);

  const [numberOfAccounts, setNumberOfAccounts] = useState<number | null>(null);
  const [mailRefundAmount, setMailRefundAmount] = useState<number | null>(null);
  const [siteRefundAmount, setSiteRefundAmount] = useState<number | null>(null);
  const [domainRefundAmount, setDomainRefundAmount] = useState<number | null>(
    null
  );
  const [allowAccountLimitModification, setAllowAccountLimitModification] =
    useState<boolean>(false);
  const [refundReason, setRefundReason] = useState<string>('');
  const [source, setSource] = useState('');
  const [otherSource, setOtherSource] = useState('');
  const [formErrors, setFormErrors] = useState(initialFormErrors);

  const token = useSelector(authTokenSelector);
  const plans: TPlan[] = useSelector(plansSelector);
  const orders: TOrder[] = useSelector(orderDetailsSelector);
  const { id: bundleId } = useSelector(bundleDetailsSelector);

  const dispatch = useDispatch();

  const currentMailPlan =
    getCurrentPlanForOrder(plans, orders, MAIL)?.[0] || null;

  const refundForOptions = useMemo(
    () => getRefundForOptions(orderRefundDetails),
    [orderRefundDetails]
  );

  const hasMailTxn = hasTransactionsForOrder(orderRefundDetails, MAIL);
  const hasSiteTxn = hasTransactionsForOrder(orderRefundDetails, SITE);
  const hasDomainTxn = hasTransactionsForOrder(orderRefundDetails, DOMAIN);
  let maxNoOfAccounts = 0;
  const formErrorMessage = intl.formatMessage({ id: 'string.empty' });
  const selectedSource =
    source === SOURCE_CHANGE_OPTIONS.OTHER_REQUEST ? otherSource : source;

  if (hasMailTxn) {
    const latestMailOrder = getLatestMailOrder(orders);
    maxNoOfAccounts =
      latestMailOrder?.mailOrderDetails?.quota?.accountsLimit || 0;
  }

  useEffect(() => {
    if (refundForOptions.length === 1) {
      // if there is only one option, we programmatically set it as the selected option
      // since we do not want to show the radio button
      setRefundFor(refundForOptions[0].value);
      setShowRefundForOptions(false);
    }
  }, [refundForOptions]);

  useEffect(() => {
    if (refundAction === REFUND_ACTIONS.DELETE_BUNDLE) {
      hasMailTxn && setMailRefundAmount(getMaxRefundableAmount(MAIL));
      hasSiteTxn && setSiteRefundAmount(getMaxRefundableAmount(SITE));
      hasDomainTxn && setDomainRefundAmount(getMaxRefundableAmount(DOMAIN));
    } else if (refundAction === REFUND_ACTIONS.DELETE_MAIL) {
      hasMailTxn && setMailRefundAmount(getMaxRefundableAmount(MAIL));
    } else if (refundAction === REFUND_ACTIONS.DELETE_SITE) {
      hasSiteTxn && setSiteRefundAmount(getMaxRefundableAmount(SITE));
    }
  }, [refundAction]);

  useEffect(() => {
    resetRefundOptions();
    setFormErrors(initialFormErrors);
  }, [refundFor, refundAction]);

  const resetRefundOptions = () => {
    setMailPlanId(null);
    setSitePlanId(null);
    setMailRefundAmount(null);
    setSiteRefundAmount(null);
    setDomainRefundAmount(null);
    setNumberOfAccounts(null);
  };

  const getMaxRefundableAmount = (orderType: string): number =>
    orderRefundDetails.find((o) => o.orderType === orderType)
      ?.refundableAmount ?? 0;

  const handleNumberOfAccountsChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value === '' ? null : +e.target.value;
    setNumberOfAccounts(value);
    setSitePlanId(null);
    setSiteRefundAmount(null);
    setDomainRefundAmount(null);
  };

  const handleUpdateSource = (e: ChangeEvent<HTMLSelectElement>) => {
    setSource(e.target.value);
  };

  const handleUpdateSourceOtherText = (e: ChangeEvent<HTMLInputElement>) => {
    setOtherSource(e.target.value);
  };

  const renderRefundForOptions = () => {
    if (!showRefundForOptions || refundForOptions.length === 1) return null;
    return refundForOptions.map((option) => (
      <div key={option.value}>
        <input
          type="radio"
          id={option.value}
          name="refundFor"
          value={option.value}
          checked={refundFor === option.value}
          onChange={() => setRefundFor(option.value)}
        />
        <label htmlFor={option.value}>{option.label}</label>
      </div>
    ));
  };

  const renderRefundActions = () => {
    return refundActionsOptions
      .filter((option) =>
        typeof option.showFor === 'string'
          ? refundFor === option.showFor
          : option.showFor
      )
      .map((option) => (
        <div key={option.value}>
          <input
            type="radio"
            id={option.value}
            name="refundAction"
            value={option.value}
            checked={refundAction === option.value}
            onChange={() => setRefundAction(option.value)}
          />
          <label htmlFor={option.value}>{option.label}</label>
        </div>
      ));
  };

  const renderPlanSelect = (
    label: string,
    selectedPlanId: number | null,
    onChange: (val: number) => void,
    planOptions: TPlan[],
    disabled: boolean = false
  ) => (
    <div className={styles.refundInputWrapper}>
      <label>{label}</label>
      <select
        value={selectedPlanId ?? ''}
        onChange={(e) => onChange(+e.target.value)}
        disabled={disabled}
      >
        <option value="">Select a plan</option>
        {planOptions.map((plan) => (
          <option key={plan.id} value={plan.id}>
            {plan.name} (ID: {plan.id}, Type: {plan.planType})
          </option>
        ))}
      </select>
    </div>
  );

  const renderRefundAmountInput = (
    label: string,
    enteredAmount: number | null,
    amount: number,
    onChange: (val: any) => void,
    showError: boolean,
    disabled: boolean = false
  ) => (
    <div className={styles.refundInputWrapper}>
      <label>{label}</label>
      <input
        type="number"
        value={enteredAmount ?? ''}
        placeholder={`Enter refund amount (max: ${getCurrencySymbol(currency.toUpperCase())}${amount})`}
        max={amount}
        onChange={(e) => {
          const value = e.target.value;
          onChange(value === '' ? null : +value);
        }}
        disabled={disabled}
        className={showError ? styles.hasError : ''}
      />
      {showError && (
        <div className={styles.error}>Please enter a valid amount</div>
      )}
    </div>
  );

  const createRefundRequestPayload = () => {
    const _refundAction = getRefundActionForPayload(
      refundAction as REFUND_ACTIONS,
      allowAccountLimitModification
    );

    const currentMailOrderId = getLatestMailOrder(orders)?.id;
    const currentSiteOrderId = getLatestSiteOrder(orders)?.id;
    const currentDomainOrderId = getLatestDomainOrder(orders)?.id;

    const config = {
      ...(_refundAction === 'DeleteOrder' && {
        orderIdToBeDeleted:
          refundAction === REFUND_ACTIONS.DELETE_MAIL
            ? currentMailOrderId
            : currentSiteOrderId,
      }),
      ...(_refundAction === 'AccountLimitChange' && {
        newAccountLimit: numberOfAccounts,
      }),
      ...(_refundAction === 'PlanChange' && {
        newOrderPlans: {
          ...(mailPlanId && {
            [currentMailOrderId]: +mailPlanId,
          }),
          ...(sitePlanId && {
            [currentSiteOrderId]: +sitePlanId,
          }),
        },
      }),
    };

    let refundAmounts: {};

    if (refundAction === REFUND_ACTIONS.DELETE_BUNDLE) {
      const maxMailRefundAmount = getMaxRefundableAmount(MAIL);
      const maxSiteRefundAmount = getMaxRefundableAmount(SITE);
      const maxDomainRefundAmount = getMaxRefundableAmount(DOMAIN);

      refundAmounts = {
        ...(currentMailOrderId &&
          maxMailRefundAmount > 0 && {
            [currentMailOrderId]: maxMailRefundAmount,
          }),
        ...(currentSiteOrderId &&
          maxSiteRefundAmount > 0 && {
            [currentSiteOrderId]: maxSiteRefundAmount,
          }),
        ...(currentDomainOrderId &&
          maxDomainRefundAmount > 0 && {
            [currentDomainOrderId]: maxDomainRefundAmount,
          }),
      };
    } else if (refundAction === REFUND_ACTIONS.DELETE_MAIL) {
      refundAmounts = {
        ...(currentMailOrderId && {
          [currentMailOrderId]: getMaxRefundableAmount(MAIL),
        }),
      };
    } else if (refundAction === REFUND_ACTIONS.DELETE_SITE) {
      refundAmounts = {
        ...(currentSiteOrderId && {
          [currentSiteOrderId]: getMaxRefundableAmount(SITE),
        }),
      };
    } else {
      // advance refund (non deletion flow)
      refundAmounts = {
        ...(mailRefundAmount && {
          [currentMailOrderId]: mailRefundAmount,
        }),
        ...(siteRefundAmount && {
          [currentSiteOrderId]: siteRefundAmount,
        }),
        ...(domainRefundAmount && {
          [currentDomainOrderId]: domainRefundAmount,
        }),
      };
    }

    return {
      bundleId,
      currency,
      startDate,
      refundAction: _refundAction,
      refundAmounts,
      ...(config ? { config } : {}),
      ...(refundReason && { reason: refundReason }),
    };
  };

  useEffect(() => {
    setAllowAccountLimitModification(mailPlanId === currentMailPlan?.id);
  }, [mailPlanId, currentMailPlan]);

  const createRefundProcessedMedusaPayload = () => {
    const _refundAction = [
      REFUND_ACTIONS.DELETE_BUNDLE,
      REFUND_ACTIONS.DELETE_MAIL,
      REFUND_ACTIONS.DELETE_SITE,
    ].includes(refundAction as REFUND_ACTIONS)
      ? 'Delete and refund'
      : 'Advance refund';

    const currentMailOrderId = getLatestMailOrder(orders)?.id;
    const currentSiteOrderId = getLatestSiteOrder(orders)?.id;
    const currentDomainOrderId = getLatestDomainOrder(orders)?.id;

    // get refund amounts from createRefundRequestPayload for each order
    const refundAmounts = createRefundRequestPayload()?.refundAmounts as any;

    // calculate total refund amount in decimal
    const totalRefundAmount = Object.values(refundAmounts).reduce(
      (acc: any, amount: any) => {
        const accNum = parseFloat(acc) || 0;
        const amtNum = parseFloat(amount) || 0;
        return accNum + amtNum;
      },
      0
    );
    return {
      refundFor: toCapitalize(refundFor),
      refundAction: _refundAction,
      totalRefundAmount: totalRefundAmount,
      mailRefundAmount: refundAmounts[currentMailOrderId] || 0,
      siteRefundAmount: refundAmounts[currentSiteOrderId] || 0,
      domainRefundAmount: refundAmounts[currentDomainOrderId] || 0,
      reason: refundReason,
      source: selectedSource,
    };
  };

  // Create payload for the medusa event of changing plan in the advanced refund flow
  const createPlanChangedMedusaPayload = () => {
    const mailPlans = getOrderPlanOptions(plans, MAIL);
    const sitePlans = getOrderPlanOptions(plans, SITE);
    const oldMailPlanType = getLatestMailOrder(orders)?.plan?.planType;
    const oldSitePlanType = getLatestSiteOrder(orders)?.plan?.planType;
    const orderType =
      refundFor === REFUND_FOR.BUNDLE
        ? [
            hasMailTxn && ORDER_TYPES.MAIL,
            hasSiteTxn && ORDER_TYPES.SITE,
          ].filter(Boolean)
        : [refundFor === REFUND_FOR.MAIL ? ORDER_TYPES.MAIL : ORDER_TYPES.SITE];

    let payload: any = {
      orderType,
      reason: refundReason,
      sourceHook: SOURCE_HOOKS.REFUND_FLOW,
      source: selectedSource,
    };

    // in the return reponse add mailPlantype and oldMailPlan type only if hasMailTxn is true and refundFor is either bundle or mail
    if (
      hasMailTxn &&
      [REFUND_FOR.BUNDLE, REFUND_FOR.MAIL].includes(refundFor as REFUND_FOR)
    ) {
      payload = {
        ...payload,
        mailPlanType:
          mailPlans?.find((p: any) => p?.id === mailPlanId)?.planType ||
          oldMailPlanType,
        oldMailPlanType,
      };
    }

    // add site plan details only if hasSiteTxn is true and refundFor is either bundle or site
    if (
      hasSiteTxn &&
      [REFUND_FOR.BUNDLE, REFUND_FOR.SITE].includes(refundFor as REFUND_FOR)
    ) {
      payload = {
        ...payload,
        sitePlanType:
          sitePlans?.find((p: any) => p?.id === sitePlanId)?.planType ||
          oldSitePlanType,
        oldSitePlanType,
      };
    }

    return payload;
  };

  const createAccountLimitChangedMedusaPayload = () => {
    const currentMailOrder = getLatestMailOrder(orders);
    return {
      order_type: [ORDER_TYPES.MAIL],
      orderStatus: currentMailOrder?.status,
      mailboxLimit: numberOfAccounts,
      oldMailboxLimit: maxNoOfAccounts,
      reason: refundReason,
      sourceHook: SOURCE_HOOKS.REFUND_FLOW,
      source: selectedSource,
    };
  };

  const areInputsValid = () => {
    const errors = { ...initialFormErrors };
    let isValid = true;
    const _refundAction = getRefundActionForPayload(
      refundAction as REFUND_ACTIONS,
      allowAccountLimitModification
    );

    if (_refundAction === 'AccountLimitChange') {
      if (
        numberOfAccounts !== null &&
        (numberOfAccounts <= 0 || numberOfAccounts > maxNoOfAccounts)
      ) {
        errors.numberOfAccounts = true;
        isValid = false;
      }

      if (!mailRefundAmount) {
        errors.mailRefundAmount = true;
        isValid = false;
      }
    } else if (_refundAction === 'PlanChange') {
      if (
        hasMailTxn &&
        [REFUND_FOR.MAIL, REFUND_FOR.BUNDLE].includes(refundFor as REFUND_FOR)
      ) {
        if (
          mailRefundAmount === null ||
          mailRefundAmount > getMaxRefundableAmount(MAIL)
        ) {
          errors.mailRefundAmount = true;
          isValid = false;
        }
      }
      if (
        hasSiteTxn &&
        [REFUND_FOR.SITE, REFUND_FOR.BUNDLE].includes(refundFor as REFUND_FOR)
      ) {
        if (
          siteRefundAmount === null ||
          siteRefundAmount > getMaxRefundableAmount(SITE)
        ) {
          errors.siteRefundAmount = true;
          isValid = false;
        }
      }
      if (hasDomainTxn && refundFor === REFUND_FOR.BUNDLE) {
        if (
          domainRefundAmount === null ||
          domainRefundAmount > getMaxRefundableAmount(DOMAIN)
        ) {
          errors.domainRefundAmount = true;
          isValid = false;
        }
      }
    }

    if (!source) {
      errors.source = true;
      isValid = false;
    }
    if (source === SOURCE_CHANGE_OPTIONS.OTHER_REQUEST && !otherSource) {
      errors.otherSource = true;
      isValid = false;
    }

    if (!source) {
      errors.source = true;
      isValid = false;
    }
    if (source === SOURCE_CHANGE_OPTIONS.OTHER_REQUEST && !otherSource) {
      errors.otherSource = true;
      isValid = false;
    }

    if (!refundReason) {
      errors.refundReason = true;
      isValid = false;
    }

    setFormErrors(errors);
    return isValid;
  };

  const handleProcessRefund = async () => {
    if (!areInputsValid()) return;
    try {
      setIsRefundProcessing(true);
      const payload = createRefundRequestPayload();
      const planChangeMedusaPayload = createPlanChangedMedusaPayload();
      const accountLimitChangeMedusaPayload =
        createAccountLimitChangedMedusaPayload();
      await dispatch(processRefund(payload, token));
      toast.success('Refund processed successfully');
      const refundProcessedMedusaPayload = createRefundProcessedMedusaPayload();
      if (refundAction === REFUND_ACTIONS.ADVANCE_REFUND) {
        if (allowAccountLimitModification) {
          await medusa.markMailboxLimitChanged(accountLimitChangeMedusaPayload);
        } else {
          await medusa.markPlanChanged(planChangeMedusaPayload);
        }
      }
      await medusa.markRefundProcessed(refundProcessedMedusaPayload);
      closeModal();
    } catch (err) {
      toast.error(
        `Something went wrong! Please try again later. Error: ${err}`
      );
      console.log(err);
    } finally {
      setIsRefundProcessing(false);
    }
  };

  if (getFullBundleRefundAmount(orderRefundDetails) <= 0) return null;

  return (
    <div className={styles.refundOptionsWrapper}>
      {showRefundForOptions && (
        <div className={styles.refundFor}>
          <label>Process refund for</label>
          <div>{renderRefundForOptions()}</div>
        </div>
      )}

      {refundFor && (
        <div className={styles.refundActions}>
          <label>Select an action</label>
          <div>{renderRefundActions()}</div>
        </div>
      )}

      {refundAction === REFUND_ACTIONS.ADVANCE_REFUND && (
        <>
          {hasMailTxn &&
            [REFUND_FOR.BUNDLE, REFUND_FOR.MAIL].includes(
              refundFor as REFUND_FOR
            ) &&
            renderPlanSelect(
              'Select Mail plan type',
              mailPlanId,
              setMailPlanId,
              getOrderPlanOptions(plans, MAIL)
            )}

          {allowAccountLimitModification &&
            [REFUND_FOR.BUNDLE, REFUND_FOR.MAIL].includes(
              refundFor as REFUND_FOR
            ) && (
              <div className={styles.refundInputWrapper}>
                <label>Modify number of accounts - Mail</label>
                <input
                  type="number"
                  value={numberOfAccounts ?? ''}
                  placeholder={`Enter number of accounts (max: ${maxNoOfAccounts})`}
                  max={maxNoOfAccounts}
                  onChange={handleNumberOfAccountsChange}
                  className={formErrors.numberOfAccounts ? styles.hasError : ''}
                />
                {formErrors.numberOfAccounts && (
                  <div className={styles.error}>
                    Please enter a valid number of accounts
                  </div>
                )}
              </div>
            )}

          {hasSiteTxn &&
            [REFUND_FOR.BUNDLE, REFUND_FOR.SITE].includes(
              refundFor as REFUND_FOR
            ) &&
            renderPlanSelect(
              'Select Site plan type',
              sitePlanId,
              setSitePlanId,
              getOrderPlanOptions(plans, SITE),
              allowAccountLimitModification
            )}

          {hasMailTxn &&
            [REFUND_FOR.BUNDLE, REFUND_FOR.MAIL].includes(
              refundFor as REFUND_FOR
            ) &&
            renderRefundAmountInput(
              'Refund Amount - Mail',
              mailRefundAmount,
              getMaxRefundableAmount(MAIL),
              setMailRefundAmount,
              formErrors.mailRefundAmount
            )}

          {hasSiteTxn &&
            [REFUND_FOR.BUNDLE, REFUND_FOR.SITE].includes(
              refundFor as REFUND_FOR
            ) &&
            renderRefundAmountInput(
              'Refund Amount - Site',
              siteRefundAmount,
              getMaxRefundableAmount(SITE),
              setSiteRefundAmount,
              formErrors.siteRefundAmount,
              allowAccountLimitModification
            )}

          {hasDomainTxn &&
            refundFor === REFUND_FOR.BUNDLE &&
            renderRefundAmountInput(
              'Refund Amount - Neo Domain',
              domainRefundAmount,
              getMaxRefundableAmount(DOMAIN),
              setDomainRefundAmount,
              formErrors.domainRefundAmount,
              allowAccountLimitModification
            )}
        </>
      )}

      {refundFor && (
        <div className={styles.refundInputWrapper}>
          <label>Specify a reason if any</label>
          <textarea
            placeholder="Enter a reason for refund"
            rows={4}
            value={refundReason}
            onChange={(e) => setRefundReason(e.target.value)}
            className={formErrors.refundReason ? styles.hasError : ''}
          />
          {formErrors.refundReason && (
            <div className={styles.error}>Please specify a valid reason</div>
          )}
        </div>
      )}
      {refundFor && (
        <div className="source-selector">
          <SourceSelector
            onSourceChange={handleUpdateSource}
            onOtherSourceChange={handleUpdateSourceOtherText}
            otherSourceError={formErrors?.otherSource ? formErrorMessage : ''}
            sourceError={formErrors?.source ? formErrorMessage : ''}
            otherSource={otherSource}
            source={source}
            errorStyles={styles.error}
          />
        </div>
      )}

      {refundFor && (
        <div className={styles.buttonsWrapper}>
          <CustomButton
            onClick={handleProcessRefund}
            isLoading={isRefundProcessing}
            type={'primary'}
            disabled={!refundFor || !refundAction}
          >
            Process Refund
          </CustomButton>
        </div>
      )}
    </div>
  );
};

export default RefundOptions;
